import { WEAVR_DATE_OF_BIRTH_FORMAT } from "@finway-group/shared/lib/consts/weavr"
import {
    type Approval,
    type ApprovalProcess,
    ApprovalStatusEnum,
    Employee,
    type Expense,
    IntegrationTypeEnum,
    type LogicalExpression,
    LogicalOperatorEnum,
    RightEnum,
    type Role,
    SystemRightEnum,
} from "@finway-group/shared/lib/models"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { isOperator } from "@finway-group/shared/lib/utils/logicalExpression.utils"
import { type TFunction } from "i18next"
import moment from "moment-timezone"
import { PROCESS_ENV } from "~Shared/config/env.utils"
import { getRemainingApprovers } from "~Shared/utils/approvalProcess.utils"
import { formatDate } from "~Shared/utils/date.utils"
import { isRightGranted } from "~Shared/utils/helper.utils"

/** Renders `YYYY-MM-DD` in the locale's date order. Anything else is dropped. */
export const formatDateOfBirth = (dateOfBirth?: string) => {
    if (!dateOfBirth) {
        return undefined
    }

    const parsedDateOfBirth = moment(dateOfBirth, WEAVR_DATE_OF_BIRTH_FORMAT, true)
    return parsedDateOfBirth.isValid() ? parsedDateOfBirth.format(moment.localeData().longDateFormat("L")) : undefined
}

/**
 * Whether an enrolled cardholder still owes a date of birth. Returns false while the profile or company is loading.
 */
export const isDateOfBirthRequired = ({ employee, isCardProgramEnabled, isFinwayAdmin }: { employee?: Employee | null; isCardProgramEnabled?: boolean; isFinwayAdmin?: boolean }) =>
    !!isCardProgramEnabled && !isFinwayAdmin && !!employee && !employee.dateOfBirth && !!employee.activeCompanyProfile?.weavrData?.userId

export const isPersonioSourcedEmployee = (employee?: Pick<Employee, "source"> | null) => Boolean(employee?.source?.id && employee.source.system === IntegrationTypeEnum.PERSONIO)

/**
 * Hold at the login gate only when the date was already missing at sign-in.
 * An enrollment that lands mid-session is asked later, at the card wizard or auth modal.
 */
export const shouldHoldAtDateOfBirthPage = ({ isDateOfBirthMissing, hasEnteredApp }: { isDateOfBirthMissing: boolean; hasEnteredApp: boolean }) =>
    isDateOfBirthMissing && !hasEnteredApp

/**
 * A Personio-sourced date stays locked because the next sync would overwrite a correction.
 * An empty date stays editable so the employee can still give one for Weavr.
 */
export const isDateOfBirthImportedFromPersonio = ({
    employee,
    isPersonioIntegrationEnabled,
    isPersonioEmployeeSyncEnabled,
    isPersonioConfigurationLoading,
}: {
    employee?: Pick<Employee, "dateOfBirth" | "source"> | null
    isPersonioIntegrationEnabled?: boolean
    isPersonioEmployeeSyncEnabled?: boolean
    isPersonioConfigurationLoading?: boolean
}) => Boolean(employee?.dateOfBirth && isPersonioSourcedEmployee(employee) && (isPersonioConfigurationLoading || (isPersonioIntegrationEnabled && isPersonioEmployeeSyncEnabled)))

export const getEmployeesByIdsInAbsenceOfLeave = (employeeIds: Array<string>, employees: Array<Employee>) =>
    employees.filter((employee: Employee) => {
        if (!employeeIds.includes(employee.id)) {
            return false
        }
        return employee.isInAbsenceOfLeave()
    })

export const getAbsentApprovers = (approvalProcess: ApprovalProcess, employees: Array<Employee>) => {
    const remainingApproversIds = getRemainingApprovers(approvalProcess).map((approval) => approval.approver?._id)
    const absentApprovers = getEmployeesByIdsInAbsenceOfLeave(remainingApproversIds, employees)
    return absentApprovers
}

interface AbsenceBlock {
    blocks: boolean
    approvers: Array<Employee>
}

const approverIdOf = (approval: Approval): string | undefined => {
    const approverId = approval.approver?._id
    if (approverId === undefined || approverId === null) {
        return undefined
    }
    return String(approverId)
}

/**
 * An expression blocks when nobody left in it can approve.
 * An OR blocks only when every operand blocks. An AND blocks when any operand does.
 * A pending approver blocks when their id is in `unavailableApproverIds`.
 */
const absenceBlockOfExpression = (expression: LogicalExpression<Approval>, employeesById: Map<string, Employee>, unavailableApproverIds: ReadonlySet<string>): AbsenceBlock => {
    if (isOperator<Approval>(expression)) {
        const operandBlocks = expression.operands.map((operand) => absenceBlockOfExpression(operand, employeesById, unavailableApproverIds))
        if (expression.operator === LogicalOperatorEnum.OR) {
            const blocks = operandBlocks.length > 0 && operandBlocks.every((operandBlock) => operandBlock.blocks)
            return { blocks, approvers: blocks ? operandBlocks.flatMap((operandBlock) => operandBlock.approvers) : [] }
        }

        return {
            blocks: operandBlocks.some((operandBlock) => operandBlock.blocks),
            approvers: operandBlocks.flatMap((operandBlock) => operandBlock.approvers),
        }
    }

    if (expression.approvedBy) {
        return { blocks: false, approvers: [] }
    }
    if (expression.rejectedBy) {
        return { blocks: true, approvers: [] }
    }

    const approverId = approverIdOf(expression)
    if (!approverId || !unavailableApproverIds.has(approverId)) {
        return { blocks: false, approvers: [] }
    }

    const employee = employeesById.get(approverId)
    return { blocks: true, approvers: employee ? [employee] : [] }
}

/**
 * Approvers whose absence stops the request from being approved as written.
 * An OR step is included only when every approver in it is unavailable.
 */
export const getApproversBlockingOnAbsence = (approvalProcess: ApprovalProcess, employees: Array<Employee>, unavailableApproverIds: ReadonlySet<string>): Array<Employee> => {
    const employeesById = new Map(employees.map((employee) => [employee.id, employee]))
    const approvers = approvalProcess.steps
        .filter((step) => step.status !== ApprovalStatusEnum.SKIPPED && step.status !== ApprovalStatusEnum.APPROVED)
        .flatMap((step) => absenceBlockOfExpression(step.approvalExpression, employeesById, unavailableApproverIds).approvers)

    return approvers.filter((approver, index) => approvers.findIndex((candidate) => candidate.id === approver.id) === index)
}

/* Admin is considered absent if it is currently or in the next in absence of leave */
export const isAdminAbsent = (startDate: Date, endDate: Date) =>
    moment().isBetween(startDate, endDate, "day", "[]") || moment(startDate).isBetween(moment(), moment().add(1, "week"), "day", "[]")

export const getAvailableReplacementsForAbsentApprover = (
    entity: Pick<Expense, "requestedBy"> & Partial<Pick<Expense, "paymentOption">>,
    employees: Array<Employee>,
    rolesMap: Map<string, Role>,
) => {
    const availableReplacementsForAbsentApprover = employees.filter((employee) => {
        const { activeCompanyProfile, id, email, absence } = employee
        if (
            email === PROCESS_ENV.FINWAY_ADMIN_EMAIL ||
            id === entity.requestedBy._id ||
            !isRightGranted(rolesMap.get(activeCompanyProfile.roleId)?.rights, RightEnum.EXPENSE__ALL__APPROVE)
        ) {
            return false
        }
        if (entity?.paymentOption === ExpensePaymentOptionEnum.SMART_CARD && !employee.isEnrolledInCardProgramOfCompany({ companyId: activeCompanyProfile.companyId })) {
            return false
        }

        return !absence || !isAdminAbsent(absence.startDate, absence.endDate)
    })

    return availableReplacementsForAbsentApprover
}

export const formatEmployeeAbsenceDate = (employee: Employee) =>
    `${moment(employee.absence?.startDate).format("Do MMM, YYYY")} - ${moment(employee.absence?.endDate).format("Do MMM, YYYY")}`

export const formatAbsenceEndDate = (endDate?: Date) => moment(endDate).format("DD/MM/YYYY")

/** Earliest return first, so the first name in the absence notice is the first person back. */
export const sortAbsentApproversByEarliestReturn = (absentApprovers: Array<Employee>) =>
    [...absentApprovers].sort((left, right) => moment(left.absence?.endDate).valueOf() - moment(right.absence?.endDate).valueOf())

/**
 * One sentence per approver, earliest return first, then the shared question.
 * A single approver stays on the existing description string, so this returns undefined.
 */
export const joinAbsentApproverSentences = ({
    absentApprovers,
    sentenceForApprover,
    waitForReturn,
}: {
    absentApprovers: Array<Employee>
    sentenceForApprover: (employee: Employee) => string
    waitForReturn: string
}) => {
    const orderedApprovers = sortAbsentApproversByEarliestReturn(absentApprovers)
    if (orderedApprovers.length <= 1) {
        return undefined
    }

    const sentences = orderedApprovers.map((employee) => `${sentenceForApprover(employee)}.`)
    return `${sentences.join(" ")} ${waitForReturn}`
}

export const isEmployeeInFutureAbsent = (employee: Employee) => (employee.absence ? moment().isBefore(employee.absence?.startDate) : false)

export const employeeAbsenceInfo = (employee: Employee, t: TFunction): string | undefined => {
    const isEmployeeAbsent = employee.isInAbsenceOfLeave()
    if (isEmployeeAbsent) {
        const daysLeft = employee.inAbsenceOfLeaveFor()
        return `${t("label:employee_absence.back_in")} ${t("label:employee_absence.day", { count: daysLeft })}`
    }

    const isFutureAbsent = isEmployeeInFutureAbsent(employee)
    if (isFutureAbsent) {
        return formatEmployeeAbsenceDate(employee)
    }
    return undefined
}

export const isSelfAdministrationOfRoleAllowed = (user: Employee, roleId: string) => !userHasAllSystemRights(user) || !user.companyProfiles?.map((cp) => cp.roleId).includes(roleId)

export const isSysAdminRightsRevocationDenied = ({ employee, sysAdminsCount, isDeletion = false }: { employee: Employee; sysAdminsCount: number; isDeletion?: boolean }) => {
    const sysAdminCountBelowOrEqualLimit = sysAdminsCount <= 2
    const nonDeletedCompanyProfilesCount = employee.companyProfiles?.filter((cp) => !cp.deleted).length ?? 0
    return sysAdminCountBelowOrEqualLimit && userHasAllSystemRights(employee) && (isDeletion ? nonDeletedCompanyProfilesCount <= 1 : true)
}

export const userHasAllSystemRights = (employee?: Employee) => (employee?.systemRights?.length ?? 0) === Object.values(SystemRightEnum).length

/**
 * Matches an employee against what the user typed into a search box, on full name or email.
 * An empty search matches everyone.
 */
export const createEmployeeSearchFilter =
    (searchValue?: string) =>
    (employee: Employee): boolean => {
        if (!searchValue) {
            return true
        }
        const search = searchValue.toLowerCase()
        return employee.getFullName().toLowerCase().includes(search) || employee.email.toLowerCase().includes(search)
    }

export const filterEmployees = (
    employees: Array<Employee>,
    selectedCompanyId: string,
    excludeDeleted: boolean = false,
    includeAutoApprover: boolean = false,
    searchValue?: string,
): Array<Employee> => {
    // Construct an array of employees, throw employee if there is no suitable company profile for the selected company id
    const employeesOfCompany = employees
        .map((employee) => {
            const employeeObject = new Employee(employee)
            const activeCompanyProfile = employeeObject.getUserCompanyProfile(selectedCompanyId)
            if (!activeCompanyProfile) {
                return undefined
            }
            employeeObject.activeCompanyProfile = activeCompanyProfile
            return employeeObject
        })
        .filter((e) => !!e) as Array<Employee>

    const filtersToApply: Array<(e: Employee) => boolean> = []

    if (!includeAutoApprover) {
        filtersToApply.push((employee) => employee.email !== PROCESS_ENV.FINWAY_ADMIN_EMAIL)
    }
    if (excludeDeleted) {
        filtersToApply.push((employee) => !!employee.activeCompanyProfile.companyId && !employee.activeCompanyProfile.deleted)
    }
    if (searchValue) {
        filtersToApply.push(createEmployeeSearchFilter(searchValue))
    }

    return employeesOfCompany.filter((e) => filtersToApply.every((filterFn) => filterFn(e)))
}

export const getSubstitutionTooltipMessage = (employee: Employee, loggedInUserId: string, t: TFunction) => {
    if (!employee.substitutedBy || !employee.absence) {
        return undefined
    }

    if (employee.substitutedBy.id === loggedInUserId) {
        return t(`tooltips:employee_absence.substituted_by_you_with_timeframe`, {
            approver: employee.getFullName(),
            startDate: formatDate(employee.absence.startDate),
            endDate: formatDate(employee.absence.endDate),
        })
    }

    return t(`tooltips:employee_absence.substituted_by_with_timeframe`, {
        approver: employee.id === loggedInUserId ? t("tooltips:employee_absence.approver_name_you") : employee.getFullName(),
        substitutedBy: `${employee.substitutedBy.firstName} ${employee.substitutedBy.lastName}`,
        startDate: formatDate(employee.absence.startDate),
        endDate: formatDate(employee.absence.endDate),
    })
}

export const createEmployeeNotDeletedFilter =
    ({ excludedUserIds }: { excludedUserIds: Array<string> }) =>
    (employee: Employee) =>
        !employee.activeCompanyProfile?.deleted || excludedUserIds.includes(employee.id)
